import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { api } from "./index.api";
import { Banner } from "../../types/banner";
import { FilterQuery } from "../../types/query";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import toastError from "../../components/notif";

function bannesApiErrorHandler(error: FetchBaseQueryError) {
  switch (error.status) {
    default:
      toastError("unkown error please try again later");
  }
}

const bannersAdapter = createEntityAdapter<Banner, string>({
  selectId: (banner: Banner) => banner._id,
});

export type GetBannersType = {
  banners: EntityState<Banner, string>;
  length: number;
  from: number;
};

const bannersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBanners: builder.query<GetBannersType, FilterQuery<Banner>>({
      query: (input) => ({
        url: `/banners`,
        params: input,
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          bannesApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      transformResponse(
        baseQueryReturnValue: { banners: any; length: number; from: number },
        meta,
        arg
      ) {
        return {
          banners: bannersAdapter.addMany(
            bannersAdapter.getInitialState(),
            baseQueryReturnValue.banners as any
          ),
          length: baseQueryReturnValue.length,
          from: baseQueryReturnValue.from,
        };
      },
      providesTags: ["banners"],
    }),

    addBanner: builder.mutation<void, FormData>({
      query: (input) => ({
        url: "/banners",
        method: "post",
        body: input,
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          bannesApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["banners"];
      },
    }),

    updateBanner: builder.mutation<void, { bannerId: string; data: FormData }>({
      query: (input) => ({
        url: `/banners/${input.bannerId}`,
        method: "PUT",
        body: input.data,
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          bannesApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["banners"];
      },
    }),

    deleteBanner: builder.mutation<void, { bannerId: string }>({
      query: (input) => ({
        url: `/banners/${input.bannerId}`,
        method: "DELETE",
      }),
      async onQueryStarted(arg, api) {
        try {
          await api.queryFulfilled;
        } catch (error: any) {
          bannesApiErrorHandler(error.error as FetchBaseQueryError);
        }
      },
      invalidatesTags: (res, err, api) => {
        if (err) return [];
        return ["banners"];
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetBannersQuery,
  useAddBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
} = bannersApi;
